import React from "react";
import uniqBy from "lodash/uniqBy";
import {TableDateCell} from "../../../components/Table/TableDateCell.jsx";
import {Dropdown, Spin} from "antd";
import {StyledButton} from "../../../components/index.jsx";
import {briefPageSize} from "./consts.jsx";
import {SystemIdToIcon} from "../../../admin/AdminOrders/consts.jsx";
import "./company-briefs.css";
import {CheckOutlined, DownOutlined} from "@ant-design/icons";
import {CommentModal} from "../../../admin/components/CommentModal";
import {CompanyDetails} from "../../../admin/AdminBriefs/utils.jsx";
import {BriefStatusLabel} from "./utils.jsx";
import {themeNamesToText} from "../../../consts.js";

export const columns = options => {
    return [
        {
            title: "#",
            key: "#",
            width: 50,
            render(_, __, idx) {
                const realIndex = (options?.currentPage - 1) * (options?.briefPageSize ?? briefPageSize) + idx + 1;
                return `${realIndex}`;
            }
        },
        {
            title: "מספר בריף",
            key: "briefNumber",
            width: 180,
            render(_, {briefNumber}) {
                return <span className="brief-number">{briefNumber}</span>;
            }
        },
        {
            title: "מערכת",
            key: "systemId",
            width: 70,
            filters: [
                ...Object.entries(themeNamesToText).map(([systemId, systemIdText]) => ({
                    text: systemIdText,
                    value: systemId?.toLowerCase()
                }))
            ],
            filteredValue: options?.briefsSystemIdOrder?.length ? options.briefsSystemIdOrder : null,
            onFilter: (value, brief) => {
                return true;
            },
            render(order, {systemId}) {
                return <img width={30} height={30} src={SystemIdToIcon[systemId]} alt="מערכת" />;
            }
        },
        {
            title: "שם חברה",
            width: 180,
            dataIndex: "companyName",
            key: "companyName",
            sorter: (r1, r2) => (r1.companyName.toLowerCase() < r2.companyName.toLowerCase() ? 1 : -1),
            render(
                companyName,
                {companyContactEmail, companyContactName, companyPhone, isCompanySubAdmin, isCompanyMaster, contactName}
            ) {
                return (
                    <CompanyDetails
                        companyName={companyName}
                        isCompanySubAdmin={isCompanySubAdmin}
                        isCompanyMaster={isCompanyMaster}
                        companyPhone={companyPhone}
                        companyContactEmail={companyContactEmail}
                        contactName={contactName}
                        companyContactName={companyContactName}
                    />
                );
            }
        },
        {
            title: "הבריף",
            key: "description",
            width: 180,
            render(_, {description}) {
                return <span className="brief-description">{description}</span>;
            }
        },
        {
            title: "תאריך יצירה",
            width: 120,
            key: "createdAt",
            sorter: ({createdAt: r1}, {createdAt: r2}) => r1 - r2,
            onHeaderCell: () => ({
                onClick: () => {
                    if (options?.setBriefsCreatedAtOrder) {
                        options.setBriefsCreatedAtOrder(prev => {
                            if (prev === "createdAt-asc") return "createdAt-desc";
                            if (prev === "createdAt-desc") return null;
                            return "createdAt-asc";
                        });
                    }
                },
                style: {cursor: "pointer"}
            }),
            render(_, {createdAt}) {
                return createdAt ? <TableDateCell date={createdAt} /> : null;
            }
        },
        {
            title: "תאריך הספקה",
            key: "dtstart",
            width: 120,
            sorter: ({dtstart: r1}, {dtstart: r2}) => r1 - r2,
            onHeaderCell: () => ({
                onClick: () => {
                    if (options?.setBriefsDtstartOrder) {
                        options.setBriefsDtstartOrder(prev => {
                            if (prev === "dtstart-asc") return "dtstart-desc";
                            if (prev === "dtstart-desc") return null;
                            return "dtstart-asc";
                        });
                    }
                },
                style: {cursor: "pointer"}
            }),
            render(_, {dtstart}) {
                return dtstart ? <TableDateCell date={dtstart} /> : null;
            }
        },
        {
            title: "תקציב לבריף",
            key: "maxPrice",
            width: 100,
            render(_, {maxPrice}) {
                return `${maxPrice}₪`;
            }
        },
        {
            title: "ניהול מכירה",
            key: "assignedCSMs",
            dataIndex: "assignedCSMs",
            width: 100,
            filters: [
                ...(options.allCSMs ?? []).map(csm => ({
                    text: csm.firstName,
                    value: csm.userId
                })),
                {text: "ללא", value: "empty"}
            ],
            onFilter: (value, brief) =>
                value === "empty"
                    ? !brief.assignedCSMs?.length
                    : brief.assignedCSMs?.some(({userId}) => userId === value),
            render(_, {briefId, updatedAt, assignedCSMs}) {
                const allowChangeCms = !!options.onChangeCSM;
                const menuOverlay = {
                    items: (options.allCSMs ?? [])?.map(csm => ({
                        key: csm.userId,
                        label: (
                            <div onClick={() => options.onChangeCSM(csm, assignedCSMs, briefId, updatedAt)}>
                                {assignedCSMs?.some(({userId}) => userId === csm.userId) ? <CheckOutlined /> : null}
                                {csm.firstName}
                            </div>
                        )
                    }))
                };

                return (
                    <Dropdown trigger={allowChangeCms ? ["click"] : []} menu={menuOverlay}>
                        <span className="admin-companies-csm-cell">
                            {options.loadingUpdateBrief === briefId ? (
                                <Spin />
                            ) : (
                                <>
                                    {assignedCSMs?.length
                                        ? assignedCSMs.map(({firstName}) => firstName).join(", ")
                                        : "ללא"}
                                    {allowChangeCms ? <DownOutlined /> : null}
                                </>
                            )}
                        </span>
                    </Dropdown>
                );
            }
        },
        {
            title: "תפעול בריף",
            width: 100,
            render(_, brief) {
                const btnOption = options.manageBriefs.find(m =>
                    typeof m?.shouldShow === "function" ? m?.shouldShow(brief) : !!m.shouldShow
                );

                if (!btnOption) return null;

                return (
                    <StyledButton
                        style={{borderRadius: "5px", backgroundColor: "var(--primary-color)", color: "white"}}
                        onClick={() => btnOption?.onClick(brief)}>
                        {btnOption?.text}
                    </StyledButton>
                );
            }
        },
        {
            title: "סטטוס",
            width: 180,
            filters: uniqBy(
                (options?.briefStatusToColorMapper ? Object.entries(options.briefStatusToColorMapper) : []).map(
                    ([statusKey, statusConfig]) => {
                        const resolveValue = value =>
                            typeof value === "function" ? value({}, options?.me ?? {}) : value;

                        const [title, , color, backgroundColor] = statusConfig;
                        const resolvedTitle = resolveValue(title) ?? statusKey;
                        const resolvedColor = resolveValue(color);
                        const resolvedBackground = resolveValue(backgroundColor);

                        return {
                            text: (
                                <span style={{color: resolvedColor, backgroundColor: resolvedBackground}}>
                                    {resolvedTitle}
                                </span>
                            ),
                            value: resolvedTitle
                        };
                    }
                ),
                "value"
            ),
            onFilter: (value, brief) => {
                const [titleResolver] = options?.briefStatusToColorMapper?.[brief?.status] ?? [];
                const resolvedTitle =
                    typeof titleResolver === "function"
                        ? titleResolver(brief, options?.me ?? {})
                        : titleResolver ?? brief?.status;

                return resolvedTitle === value;
            },
            render(_, brief) {
                return (
                    <BriefStatusLabel
                        brief={brief}
                        briefStatusToColorMapper={options?.briefStatusToColorMapper}
                        me={options?.me}
                    />
                );
            }
        },
        ...(options.onSaveComment
            ? [
                  {
                      width: 10,
                      render(_, brief) {
                          return (
                              <CommentModal
                                  comment={brief.adminComment}
                                  onSave={comment => options.onSaveComment(comment, brief.briefId, brief.updatedAt)}
                              />
                          );
                      }
                  }
              ]
            : [])
    ];
};
